// pages/_app.js
import { useEffect, Component, Suspense } from 'react';
import { useRouter } from 'next/router';

import Head from 'next/head';
import dynamic from 'next/dynamic';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Global error handler for DOMException and other errors
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.name === 'DOMException') {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event) => {
    if (event.error && event.error.name === 'DOMException') {
      event.preventDefault();
    }
  });
}

const Toaster = dynamic(() =>
  import('react-hot-toast').then(mod => ({ default: mod.Toaster })),
  { ssr: false }
);

// Layouts - Regular Layout loaded immediately, AdminLayout lazy loaded
import Layout from '../components/layout/Layout';
import ErrorBoundary from '../components/ErrorBoundary';
const AdminLayout = dynamic(() => import('../components/admin/AdminLayout'), {
  loading: () => <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
  </div>,
  ssr: false
});

// Context providers (these remain named imports as per your existing code, assuming they are named exports)
import { AuthProvider } from '../hooks/useAuth';
// Lazy load SocketProvider (WebSocket not needed on SSR)
const SocketProvider = dynamic(() => 
  import('../context/SocketContext').then(mod => ({ default: mod.SocketProvider })), 
  { ssr: false, loading: () => <div /> }
);
import { CartProvider } from '../context/CartContext';
import { ThemeProvider } from '../context/ThemeContext';
import { TranslationProvider } from '../context/TranslationContext';

// Styles
import '../styles/globals.css';

// Query client with optimized settings - lighter config for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 1 * 60 * 1000, // 1 minute - fresher data
      gcTime: 5 * 60 * 1000 // 5 minutes - reduced memory usage
    },
  },
});

// ErrorBoundary is now imported from ../components/ErrorBoundary

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const { pathname } = router;
  const isAdminPage = pathname.startsWith('/admin');
  const isAdminLoginPage = pathname === '/admin/login';
  const isAuthPage = pathname.startsWith('/auth');

  // Scroll restoration - save and restore scroll position on back/forward navigation
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Take manual control of scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Flag to track if navigation is from back/forward button
    let isBackNavigation = false;

    const saveScrollPosition = (url) => {
      sessionStorage.setItem(`scrollPos:${url}`, window.scrollY.toString());
    };

    const restoreScrollPosition = (url) => {
      const savedPosition = sessionStorage.getItem(`scrollPos:${url}`);
      if (savedPosition) {
        // Use requestAnimationFrame for smoother restoration after DOM updates
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
        });
      }
    };

    // Save position before route change
    const handleRouteChangeStart = () => {
      saveScrollPosition(router.asPath);
    };

    // Restore position after route change completes (only on back/forward)
    const handleRouteChangeComplete = (url) => {
      if (isBackNavigation) {
        restoreScrollPosition(url);
        isBackNavigation = false;
      } else {
        // Forward navigation - scroll to top
        window.scrollTo(0, 0);
      }
    };

    // Popstate fires before routeChangeStart on back/forward navigation
    const handlePopState = () => {
      isBackNavigation = true;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    window.addEventListener('popstate', handlePopState);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [router]);

  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) jssStyles.parentElement.removeChild(jssStyles);

    // Error handlers for production
    const handleGlobalError = () => {};
    const handleUnhandledRejection = () => {};
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Initialize performance optimizations
    if (process.env.NODE_ENV === 'production') {
      // Initialize quick loader immediately
      import('../utils/quickLoad').then(({ quickLoader }) => {
        quickLoader.init();
      });
      
      // Initialize preloader for better resource loading
      import('../utils/preloader').then(({ preloader }) => {
        preloader.init();
      });
    } else {
      // Development optimizations
      import('../utils/quickLoad').then(({ quickLoader }) => {
        quickLoader.init();
      });
    }

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Import and initialize performance monitoring
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      // Initialize Firebase Performance Monitoring
      import('../config/firebase').catch(() => {});
      
      import('../utils/performanceMonitor').then(({ perfMonitor }) => {
        // Only measure on initial load
        if (pathname === '/') {
          perfMonitor.measureCoreWebVitals();
        }
      });
    }
    
  }, [pathname]);

  // Development-only logging - completely disabled in production
  // All console.log statements are handled by secureLogger utility

  return (
    <QueryClientProvider client={queryClient}>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#b8d2b3" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        
        {/* Google Fonts preconnect/stylesheet are loaded once in _document.js */}

        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-S2F96PRVVV"></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-S2F96PRVVV');
            `,
          }}
        />
      </Head>

      <ErrorBoundary>
        <TranslationProvider>
          <AuthProvider>
            <SocketProvider>
              <CartProvider>
                  <ThemeProvider>
                    {isAdminPage && !isAdminLoginPage ? (
                      <AdminLayout>
                        <Component {...pageProps} />
                      </AdminLayout>
                    ) : (
                      <Layout>
                        <Component {...pageProps} />
                      </Layout>
                    )}
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: 'var(--bg-tertiary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        maxWidth: '500px',
                        padding: '12px 16px',
                      },
                      success: {
                        duration: 3000,
                        iconTheme: { primary: 'var(--success-color)', secondary: 'white' },
                        style: { background: 'var(--success-light)', color: 'var(--success-color)' },
                      },
                      error: {
                        duration: 4000,
                        iconTheme: { primary: 'var(--error-color)', secondary: 'white' },
                        style: { background: 'var(--error-light)', color: 'var(--error-color)' },
                      },
                      loading: {
                        duration: Infinity,
                        iconTheme: { primary: 'var(--info-color)', secondary: 'white' },
                        style: { background: 'var(--info-light)', color: 'var(--info-color)' },
                      },
                    }}
                    containerStyle={{ top: '20px', right: '20px' }}
                  />
                </ThemeProvider>
              </CartProvider>
            </SocketProvider>
          </AuthProvider>
        </TranslationProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default MyApp;
